import path from 'node:path'
import katex from 'katex'
import { useEffect, useRef, useState } from 'react'
import { Link, redirect } from 'react-router'
import { MCQExercise } from '#app/components/exercises/mcq-exercise.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { STUDY_HOME_PATH } from '#app/utils/app-routes.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import {
	getLessonVisualIdentityFromPackageDir,
	loadLessonVisualManifestForPackageDir,
	resolveLessonVisualUri,
} from '#app/utils/lesson-visuals.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

// ---------------------------------------------------------------------------
// KaTeX pre-rendering (server-only, stays out of the client bundle)
// ---------------------------------------------------------------------------
function tex(formula: string, displayMode = false) {
	return katex.renderToString(formula, { displayMode, throwOnError: false })
}

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const wantsLanding = url.searchParams.has('landing')
	if (!wantsLanding) {
		const userId = await getUserId(request)
		if (userId) throw redirect(STUDY_HOME_PATH)
	}

	const packageDir = path.resolve(
		process.cwd(),
		'content/curriculum/S1/functii-elementare/functia-de-gradul-2',
	)
	const identity = getLessonVisualIdentityFromPackageDir(packageDir)
	const manifest = await loadLessonVisualManifestForPackageDir(packageDir)

	return {
		math: {
			// Hero floating decorations
			floats: [
				tex('\\Delta = b^2 - 4ac'),
				tex('\\int_0^1 x^2 \\, dx'),
				tex('\\begin{pmatrix} 1 & 2 \\\\ 3 & 4 \\end{pmatrix}'),
				tex('\\lim_{n \\to \\infty}'),
				tex("f'(x) = 2x"),
				tex('\\sum_{k=1}^{n} k'),
			],
			// Lesson showcase
			quadraticDef: tex('f(x) = ax^2 + bx + c,\\quad a \\neq 0', true),
			vertexFormula: tex(
				'x_V = -\\dfrac{b}{2a},\\quad y_V = -\\dfrac{\\Delta}{4a}',
				true,
			),
			// MCQ exercise
			exerciseFunc: tex('f(x) = x^2 - 4x + 3'),
			optionA: tex('V(2,\\,-1)'),
			optionB: tex('V(-2,\\,1)'),
			optionC: tex('V(4,\\,3)'),
			optionD: tex('V(1,\\,0)'),
			mcqSolution: tex(
				'x_V = \\dfrac{4}{2} = 2,\\quad y_V = 4 - 8 + 3 = -1',
				true,
			),
		},
		parabolaVisual: {
			poster: resolveLessonVisualUri({
				uri: 'visual://parabola-varf-si-axa/poster',
				identity,
				manifest,
			}),
			mp4: resolveLessonVisualUri({
				uri: 'visual://parabola-varf-si-axa/mp4',
				identity,
				manifest,
			}),
		},
	}
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Matematix — Pregătire pentru Bacalaureat la Matematică' },
	{
		name: 'description',
		content:
			'Lecții, exerciții și recapitulări organizate pe subiectele examenului de Bacalaureat la Matematică. Teorie cu formule, feedback instant și plan de studiu adaptat.',
	},
]

export default function Index({ loaderData }: Route.ComponentProps) {
	const user = useOptionalUser()
	const { math, parabolaVisual } = loaderData

	return (
		<main className="overflow-hidden">
			<HeroSection user={!!user} formulas={math.floats} />
			<ContentShowcase math={math} parabolaVisual={parabolaVisual} />
			<DayJourneySection />
			<BenefitsSection />
			<CtaSection user={!!user} />
		</main>
	)
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

const FLOAT_POSITIONS = [
	{ left: '8%', top: '18%', delay: '0s' },
	{ left: '82%', top: '12%', delay: '1.2s' },
	{ left: '78%', top: '68%', delay: '2.4s' },
	{ left: '12%', top: '72%', delay: '0.6s' },
	{ left: '88%', top: '42%', delay: '1.8s' },
	{ left: '4%', top: '46%', delay: '3s' },
] as const

function HeroSection({
	user,
	formulas,
}: {
	user: boolean
	formulas: string[]
}) {
	return (
		<section className="relative">
			{/* Dot grid background */}
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.35]"
				aria-hidden="true"
				style={{
					backgroundImage:
						'radial-gradient(circle, var(--border) 1px, transparent 1px)',
					backgroundSize: '32px 32px',
				}}
			/>

			{/* Floating math decorations */}
			<div
				className="pointer-events-none absolute inset-0 overflow-hidden"
				aria-hidden="true"
			>
				{formulas.map((html, i) => {
					const pos = FLOAT_POSITIONS[i]!
					return (
						<span
							key={i}
							className="absolute animate-landing-float text-sm opacity-[0.07] sm:text-base"
							style={{
								left: pos.left,
								top: pos.top,
								animationDelay: pos.delay,
							}}
							dangerouslySetInnerHTML={{ __html: html }}
						/>
					)
				})}
			</div>

			<div className="container relative z-10 flex min-h-[min(75vh,720px)] flex-col items-center justify-center py-20 text-center">
				<h1 className="animate-slide-top font-display max-w-[20ch] text-4xl font-bold tracking-tight [animation-fill-mode:backwards] sm:text-5xl md:text-6xl">
					Matematica de Bac{' '}
					<span className="bg-gradient-to-r from-section-s1 via-section-s2 to-section-s3 bg-clip-text text-transparent">
						pe pilot automat
					</span>
				</h1>

				<p className="mt-6 max-w-xl animate-slide-top text-lg text-muted-foreground [animation-delay:0.2s] [animation-fill-mode:backwards]">
					Pași concreți, feedback pe loc, recapitulări frecvente.<br /> Deschizi
					aplicația și știi exact ce ai de făcut.
				</p>

				<div className="mt-8 flex gap-4 animate-slide-top [animation-delay:0.4s] [animation-fill-mode:backwards]">
					{user ? (
						<Button asChild size="lg">
							<Link to={STUDY_HOME_PATH}>Începe</Link>
						</Button>
					) : (
						<>
							<Button asChild size="lg">
								<Link to="/signup">Începe gratuit</Link>
							</Button>
							<Button asChild variant="secondary" size="lg">
								<Link to="/login">Conectează-te</Link>
							</Button>
						</>
					)}
				</div>
			</div>
		</section>
	)
}

// ---------------------------------------------------------------------------
// Content showcase — lesson excerpt + interactive exercises
// ---------------------------------------------------------------------------

type LoaderData = Awaited<ReturnType<typeof loader>>
type Math = LoaderData['math']
type ParabolaVisual = LoaderData['parabolaVisual']
const SHOWCASE_SLIDES = [
	{ id: 'lesson', label: 'Lecție' },
	{ id: 'exercise', label: 'Exercițiu' },
] as const
type ShowcaseSlideId = (typeof SHOWCASE_SLIDES)[number]['id']

function ContentShowcase({
	math,
	parabolaVisual,
}: {
	math: Math
	parabolaVisual: ParabolaVisual
}) {
	const [activeSlide, setActiveSlide] = useState<ShowcaseSlideId>('lesson')
	const [viewportHeight, setViewportHeight] = useState<number | null>(null)
	const lessonSlideRef = useRef<HTMLDivElement>(null)
	const exerciseSlideRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const activeElement =
			activeSlide === 'lesson'
				? lessonSlideRef.current
				: exerciseSlideRef.current
		if (!activeElement) return

		const updateHeight = () => {
			setViewportHeight(activeElement.offsetHeight)
		}

		updateHeight()

		if (typeof ResizeObserver === 'undefined') return

		const observer = new ResizeObserver(updateHeight)
		observer.observe(activeElement)

		return () => observer.disconnect()
	}, [activeSlide])

	function moveSlide(direction: 'prev' | 'next') {
		const currentIndex = SHOWCASE_SLIDES.findIndex(
			(slide) => slide.id === activeSlide,
		)
		const nextIndex =
			direction === 'next'
				? (currentIndex + 1) % SHOWCASE_SLIDES.length
				: (currentIndex - 1 + SHOWCASE_SLIDES.length) % SHOWCASE_SLIDES.length
		setActiveSlide(SHOWCASE_SLIDES[nextIndex]!.id)
	}

	function handleTabKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
		if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
			event.preventDefault()
			moveSlide('next')
		}
		if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
			event.preventDefault()
			moveSlide('prev')
		}
	}

	return (
		<section className="border-t py-20">
			<div className="container">
				<h2 className="text-h3 mb-10 text-center">
					Teoria și exercițiile, într-un singur loc
				</h2>

				<div className="mx-auto max-w-3xl">
					<div className="mb-6 flex justify-center">
						<div
							role="tablist"
							aria-label="Previzualizare lecție și exercițiu"
							onKeyDown={handleTabKeyDown}
							className="surface-1 inline-flex items-center gap-1 rounded-full border p-1"
						>
							{SHOWCASE_SLIDES.map((slide) => {
								const isActive = activeSlide === slide.id
								return (
									<button
										key={slide.id}
										id={`showcase-tab-${slide.id}`}
										type="button"
										role="tab"
										aria-selected={isActive}
										aria-controls={`showcase-panel-${slide.id}`}
										tabIndex={isActive ? 0 : -1}
										onClick={() => setActiveSlide(slide.id)}
										className={cn(
											'rounded-full px-4 py-2 text-sm font-medium transition-colors',
											isActive
												? 'bg-foreground text-background'
												: 'text-muted-foreground hover:text-foreground',
										)}
									>
										{slide.label}
									</button>
								)
							})}
						</div>
					</div>

					<div
						className="overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
						style={
							viewportHeight === null ? undefined : { height: viewportHeight }
						}
					>
						<div
							className="flex items-start transition-transform duration-400 ease-out motion-reduce:transition-none"
							style={{
								transform:
									activeSlide === 'lesson'
										? 'translateX(0%)'
										: 'translateX(-100%)',
							}}
						>
							<div
								ref={lessonSlideRef}
								id="showcase-panel-lesson"
								role="tabpanel"
								aria-labelledby="showcase-tab-lesson"
								aria-hidden={activeSlide !== 'lesson'}
								inert={activeSlide !== 'lesson'}
								className="w-full shrink-0"
							>
								<LandingLessonDemo
									math={math}
									parabolaVisual={parabolaVisual}
								/>
							</div>

							<div
								ref={exerciseSlideRef}
								id="showcase-panel-exercise"
								role="tabpanel"
								aria-labelledby="showcase-tab-exercise"
								aria-hidden={activeSlide !== 'exercise'}
								inert={activeSlide !== 'exercise'}
								className="w-full shrink-0"
							>
								<LandingMcqDemo math={math} />
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

// ---------------------------------------------------------------------------
// Interactive exercise demos (local state, no server)
// ---------------------------------------------------------------------------

function LandingLessonDemo({
	math,
	parabolaVisual,
}: {
	math: Math
	parabolaVisual: ParabolaVisual
}) {
	return (
		<div className="surface-1 card-showcase flex flex-col border">
			<div className="mb-4 flex items-center gap-2">
				<SectionPill code="S1" />
				<span className="text-xs text-muted-foreground">
					Lecție · Funcția de gradul II
				</span>
			</div>

			<h3 className="text-h5 mb-3">Funcția de gradul II</h3>

			<p className="text-sm text-muted-foreground">Forma generală:</p>

			<div
				className="math-prose"
				dangerouslySetInnerHTML={{ __html: math.quadraticDef }}
			/>

			<p className="mt-4 text-sm text-muted-foreground">
				Coordonatele vârfului parabolei:
			</p>

			<div
				className="math-prose"
				dangerouslySetInnerHTML={{ __html: math.vertexFormula }}
			/>

			<div className="mt-6 rounded-xl border bg-muted/40 p-3">
				<video
					className="h-auto w-full rounded-md"
					src={parabolaVisual.mp4}
					poster={parabolaVisual.poster}
					autoPlay
					muted
					loop
					playsInline
					preload="metadata"
					aria-label="Animație: vârful și axa de simetrie a parabolei f(x) = x² − 4x + 3"
				/>
			</div>
		</div>
	)
}

function LandingMcqDemo({ math }: { math: Math }) {
	const [selected, setSelected] = useState('')
	const [submitted, setSubmitted] = useState(false)
	const isCorrect = submitted ? selected === 'a' : null

	const options = [
		{ id: 'a', text: math.optionA },
		{ id: 'b', text: math.optionB },
		{ id: 'c', text: math.optionC },
		{ id: 'd', text: math.optionD },
	]

	return (
		<div className="surface-1 card-showcase border">
			<div className="mb-4 flex items-center gap-2">
				<SectionPill code="S1" />
				<span className="text-xs text-muted-foreground">
					Exercițiu · Funcția de gradul II
				</span>
			</div>

			<p className="text-body-sm mb-4">
				Funcția{' '}
				<span dangerouslySetInnerHTML={{ __html: math.exerciseFunc }} /> are
				vârful parabolei în punctul:
			</p>

			<MCQExercise
				options={options}
				selectedAnswer={selected}
				onSelect={setSelected}
				onSubmit={() => setSubmitted(true)}
				submitted={submitted}
				isCorrect={isCorrect}
				correctOptionId="a"
			/>

			<div
				className={cn(
					'mt-4 grid overflow-hidden border-t transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
					submitted ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
				)}
				aria-hidden={!submitted}
			>
				<div
					className={cn(
						'overflow-hidden pt-4 transition-[transform] duration-300 ease-out motion-reduce:transition-none',
						submitted ? 'translate-y-0' : '-translate-y-2',
					)}
				>
					<p className="mb-1 text-xs font-semibold text-muted-foreground">
						Soluție
					</p>
					<div
						className="math-prose text-sm"
						dangerouslySetInnerHTML={{ __html: math.mcqSolution }}
					/>
				</div>
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// Benefits — how the app helps
// ---------------------------------------------------------------------------

const BENEFITS = [
	{
		title: 'Te învățăm exact ce îți aduce puncte.',
		description:
			'Afli cum să îți asiguri punctajul scriind formulele corecte înainte de a calcula și cum să eviți capcanele clasice în care se pierd puncte.',
	},
	{
		title: 'Înțelegi ceea ce calculezi',
		description:
			'Folosim animații și grafice interactive pentru fiecare concept greu. Vezi exact ce înseamnă intersecția cu abscisa sau monotonia unei derivate, ca să înțelegi logica din spatele exercițiilor.',
	},
	{
		title: 'Dezvolți reflexe exersând constant',
		description:
			'Prin recapitulare periodică, incertitudinea se transformă în instinct de rezolvare. Este destul să citești enunțul și mintea ta va ști automat ce pași trebuie urmați.',
	},
] as const

function BenefitsSection() {
	const [index, setIndex] = useState(0)
	const count = BENEFITS.length
	const activeBenefit = BENEFITS[index] ?? BENEFITS[0]

	return (
		<section className="border-t py-20">
			<div className="container">
				<h2 className="text-h3 mb-10 text-center">De ce funcționează</h2>

				<div className="mx-auto max-w-3xl">
					<div className="relative overflow-hidden">
						<div
							className="flex transition-transform duration-300 ease-out"
							style={{ transform: `translateX(-${index * 100}%)` }}
						>
							{BENEFITS.map((b) => (
								<div
									key={b.title}
									className="w-full shrink-0 px-2 sm:px-4"
									aria-hidden={activeBenefit?.title !== b.title}
								>
									<h3 className="mb-3 font-semibold">{b.title}</h3>
									<p className="text-sm text-muted-foreground">
										{b.description}
									</p>
								</div>
							))}
						</div>
					</div>

					<div className="mt-6 flex items-center justify-center gap-4">
						<button
							type="button"
							onClick={() => setIndex((i) => (i - 1 + count) % count)}
							className="hover:bg-muted text-muted-foreground hover:text-foreground inline-flex size-9 items-center justify-center rounded-full border transition"
							aria-label="Anterior"
						>
							<Icon name="arrow-left" className="size-4" />
						</button>
						<div className="flex items-center gap-2">
							{BENEFITS.map((b, i) => (
								<button
									key={b.title}
									type="button"
									onClick={() => setIndex(i)}
									aria-label={`Slide ${i + 1}`}
									aria-current={i === index}
									className={cn(
										'size-2 rounded-full transition',
										i === index
											? 'bg-foreground w-6'
											: 'bg-muted-foreground/40 hover:bg-muted-foreground/70',
									)}
								/>
							))}
						</div>
						<button
							type="button"
							onClick={() => setIndex((i) => (i + 1) % count)}
							className="hover:bg-muted text-muted-foreground hover:text-foreground inline-flex size-9 items-center justify-center rounded-full border transition"
							aria-label="Următor"
						>
							<Icon name="arrow-right" className="size-4" />
						</button>
					</div>
				</div>
			</div>
		</section>
	)
}

// ---------------------------------------------------------------------------
// Day journey — what a study day looks like
// ---------------------------------------------------------------------------

const DAY_STEPS = [
	{
		step: '1',
		rail: 'section-rail-s1',
		numberClass: 'text-section-s1',
		title: 'Știi mereu ce urmează',
		description:
			'Fără timp pierdut întrebându-te „Eu ce învăț azi?”. Când intri în aplicație ai un traseu clar și personalizat: o lecție nouă, o recapitulare rapidă sau o simulare pentru a-ți testa reflexele.',
	},
	{
		step: '2',
		rail: 'section-rail-s2',
		numberClass: 'text-section-s2',
		title: 'Înveți pas cu pas',
		description:
			'Fără explicații greoaie, doar lecții scurte și la obiect. Te-ai blocat la un exercițiu? Primești indicii exact la pasul la care ai nevoie și soluții explicate logic, de la prima formulă până la rezultatul final.',
	},
	{
		step: '3',
		rail: 'section-rail-s3',
		numberClass: 'text-section-s3',
		title: 'Nu uiți ce ai învățat',
		description:
			'Sistemul nostru inteligent știe ce noțiuni stăpânești și unde mai ai de lucrat. Reiei conceptele prin exerciții targetate, până când ajungi să le rezolvi din reflex, fără dificultăți.',
	},
] as const

function DayJourneySection() {
	return (
		<section className="bg-muted/30 py-20">
			<div className="container">
				<h2 className="text-h3 mb-10 text-center">
					Cum te ajutăm să înveți
				</h2>

				<div className="mx-auto flex max-w-3xl flex-col gap-10">
					{DAY_STEPS.map((s) => (
						<div
							key={s.step}
							className={cn(
								'grid grid-cols-[4rem_minmax(0,1fr)] items-center gap-5 py-1 pl-5 sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:gap-6 sm:pl-6',
								s.rail,
							)}
						>
							<div
								className={cn(
									'flex justify-center font-display text-5xl font-bold leading-none [font-variant-numeric:tabular-nums] sm:text-6xl',
									s.numberClass,
								)}
							>
								{s.step}
							</div>
							<div className="min-w-0">
								<h3 className="mb-2 font-semibold">{s.title}</h3>
								<p className="text-sm text-muted-foreground">{s.description}</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}

// ---------------------------------------------------------------------------
// CTA
// ---------------------------------------------------------------------------

function CtaSection({ user }: { user: boolean }) {
	return (
		<section className="py-20">
			<div className="container text-center">
				<h2 className="text-h3 mb-3">Gata de treabă?</h2>
				<p className="mx-auto mb-8 max-w-md text-muted-foreground">
					Creează-ți cont, alege profilul și începe cu prima lecție
					recomandată.
				</p>
				<Button asChild size="lg">
					<Link to={user ? STUDY_HOME_PATH : '/signup'}>
						{user ? 'Începe' : 'Creează cont gratuit'}
					</Link>
				</Button>
				<p className="mt-10 text-sm text-muted-foreground">
					Ai întrebări despre platformă, cont sau abonament? Scrie-ne la{' '}
					<a
						href="mailto:contact@matematix.app"
						className="text-foreground font-medium underline underline-offset-2"
					>
						contact@matematix.app
					</a>
				</p>
			</div>
		</section>
	)
}

// ---------------------------------------------------------------------------
// Reusable bits
// ---------------------------------------------------------------------------

const SECTION_PILL_STYLES = {
	S1: 'border-section-s1/35 bg-section-s1-light/55 text-section-s1',
	S2: 'border-section-s2/35 bg-section-s2-light/55 text-section-s2',
	S3: 'border-section-s3/35 bg-section-s3-light/55 text-section-s3',
} as const

function SectionPill({ code }: { code: 'S1' | 'S2' | 'S3' }) {
	const labels = { S1: 'Subiectul I', S2: 'Subiectul II', S3: 'Subiectul III' }
	return (
		<span
			className={cn(
				'inline-flex items-center rounded-lg border px-2.5 py-1 text-xs font-semibold',
				SECTION_PILL_STYLES[code],
			)}
		>
			{labels[code]}
		</span>
	)
}
